import { apiClient } from './client';
import { API_CONFIG } from './config';

// --- Service Types (Categories) ---

export type ServiceTypeEntity = {
  serviceTypeId: string;
  projectId: string;
  type: string;
  image?: string;
  sequence?: number;
  imageFile?: { s3Url: string };
  services?: ServiceEntity[];
};

// --- Services ---

export type ServiceEntity = {
  serviceId: string;
  projectId: string;
  serviceTypeId?: string;
  serviceType?: string;
  code: string;
  title: string;
  description?: string;
  remark?: string;
  price: number;
  currency: string;
  discount?: number;
  commissionPercentage?: number;
  unitType?: string;
  unitValue?: string;
  status: string;
  image?: string;
  imageFile?: { s3Url: string };
  allowAttendedBy?: string[];
  allowExternalBooking?: boolean;
  isSubscription?: boolean;
  subscriptionLimit?: number;
  subscriptionDuration?: number;
  validFrom?: string;
  validTo?: string;
  sequence?: number;
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
};

// --- Booking Items (line items — multi-service per booking) ---

export type ServiceBookingItemEntity = {
  serviceBookingItemId: string;
  serviceBookingId: string;
  serviceId: string;
  attendedBy?: string[];
  serviceObject?: ServiceEntity;
  price: number;
  quantity: number;
  subtotal: number;
  subscriptionId?: string;
  subscriptionExpiredAt?: string;
  subscriptionCount?: number;
  createdAt: string;
  updatedAt: string;
};

// --- Bookings ---

export type BookingEntity = {
  serviceBookingId: string;
  bookingId?: string;
  projectId: string;
  serviceId: string;
  service?: ServiceEntity;
  serviceIdArray?: string[];
  serviceJson?: ServiceEntity[];
  customerId: string;
  customer?: { name: string; email?: string; phoneNo?: string };
  attendedBy?: string;
  attendedByUser?: { name: string };
  attendedByArray?: string[];
  attendedAddress?: string;
  isExternal: boolean;
  reservationDuration: number;
  reservationDateTime: string;
  reservationEndDateTime?: string;
  totalPrice: number;
  paymentStatus: string;
  paymentMethod?: string;
  status: string;
  remark?: string;
  customForm?: unknown;
  metadata?: { serviceMap?: ServiceMapEntry[]; [key: string]: unknown };
  serviceBookingItem?: ServiceBookingItemEntity;
  createdAt: string;
  updatedAt: string;
};

export type ServiceMapEntry = {
  serviceId: string;
  attendedBy: string[];
  price?: number;
  serviceBookingItemId?: string;
};

// --- Custom Forms ---

export type CustomFormEntity = {
  serviceCustomFormId: string;
  projectId: string;
  serviceIds?: string[];
  applyToAll?: boolean;
  type: string;
  key: string;
  label: string;
  choices?: string[];
  required?: boolean;
  sequence?: number;
  instruction?: string;
  createdAt: string;
  updatedAt: string;
};

// --- Monthly Sales Report ---

export type BreakdownItem = {
  key: string;
  label?: string;
  count: number;
  amount: number;
};

export type MonthlySalesResponse = {
  month: number;
  year: number;
  summary: {
    revenue: number;
    bookingCount: number;
    paidRevenue: number;
    unpaidRevenue: number;
    expenses: number;
    expenseCount: number;
    commission: number;
    commissionCount: number;
    netProfit: number;
    averageBookingValue: number;
    margin: number;
  };
  previousMonth: {
    revenue: number;
    bookingCount: number;
    paidRevenue: number;
    unpaidRevenue: number;
    expenses: number;
    expenseCount: number;
    commission: number;
    commissionCount: number;
    netProfit: number;
  };
  breakdowns: {
    byPaymentMethod: BreakdownItem[];
    byStaff: BreakdownItem[];
    byExpensePaymentMethod: BreakdownItem[];
    topServices: BreakdownItem[];
  };
};

// --- API Class ---

export type BookingsListResponse = {
  data: BookingEntity[];
  total: number;
  skip: number;
  limit: number;
};

class BookingApi {
  private svc = API_CONFIG.endpoints.services;
  private v2 = API_CONFIG.endpoints.servicesV2;
  private v3 = API_CONFIG.endpoints.servicesV3;

  // Service Types
  listServiceTypes() {
    return apiClient.get<ServiceTypeEntity[]>(this.svc.types);
  }
  createServiceType(data: Partial<ServiceTypeEntity>) {
    return apiClient.post<ServiceTypeEntity>(this.svc.createType, data);
  }
  updateServiceType(id: string, data: Partial<ServiceTypeEntity>) {
    return apiClient.put<ServiceTypeEntity>(this.svc.updateType(id), data);
  }
  deleteServiceType(id: string) {
    return apiClient.delete(this.svc.deleteType(id));
  }

  // Services
  listServices() {
    return apiClient.get<ServiceEntity[]>(this.svc.list);
  }
  createService(data: Partial<ServiceEntity>) {
    return apiClient.post<ServiceEntity>(this.svc.create, data);
  }
  updateService(id: string, data: Partial<ServiceEntity>) {
    return apiClient.put<ServiceEntity>(this.svc.update(id), data);
  }
  deleteService(id: string) {
    return apiClient.delete(this.svc.delete(id));
  }
  duplicateService(id: string) {
    return apiClient.post<ServiceEntity>(this.svc.duplicate(id), {});
  }
  updateServiceSequence(id: string, sequence: number) {
    return apiClient.patch<ServiceEntity>(this.svc.updateSequence(id), { sequence });
  }

  // Monthly Sales Report
  monthlySales(month?: number, year?: number) {
    const params: Record<string, number> = {};
    if (month != null) params.month = month;
    if (year != null) params.year = year;
    return apiClient.get<MonthlySalesResponse>(this.v2.monthlySales, { params });
  }

  // Bookings (v3 — paginated, returns { data, total, skip, limit })
  listBookings(params?: {
    skip?: number; limit?: number; search?: string;
    dateFrom?: string; dateTo?: string; status?: string;
    paymentStatus?: string; paymentMethod?: string; attendedBy?: string;
  }) {
    return apiClient.get<BookingsListResponse>(this.v3.bookings, { params });
  }
  getBooking(id: string) {
    return apiClient.get<BookingEntity>(this.svc.bookingById(id));
  }
  updateBooking(id: string, data: Record<string, unknown>) {
    return apiClient.patch<BookingEntity>(this.v2.updateBooking(id), data);
  }
  updateBookingStatus(id: string, status: string) {
    return apiClient.patch(this.v2.updateBookingStatus(id), { status });
  }

  // Summary (today/tomorrow/this week)
  bookingSummary(utcDateTime?: string) {
    const params = utcDateTime ? { utcDateTime } : undefined;
    return apiClient.get<BookingEntity[]>(this.svc.bookingSummary, { params });
  }

  // Recent bookings (v3 — paginated)
  recentBookings(params?: { limit?: number; skip?: number; status?: string; search?: string }) {
    return apiClient.get<BookingsListResponse>(this.v3.recentBookings, { params });
  }

  // Attended By
  listAttendedBy() {
    return apiClient.get<Array<{ userId: string; name: string }>>(this.svc.attendedBy);
  }

  // Custom Forms
  listCustomForms() {
    return apiClient.get<CustomFormEntity[]>(this.svc.customForms);
  }
  createCustomForm(data: Partial<CustomFormEntity>) {
    return apiClient.post<CustomFormEntity>(this.svc.createCustomForm, data);
  }
  updateCustomForm(id: string, data: Partial<CustomFormEntity>) {
    return apiClient.put<CustomFormEntity>(this.svc.updateCustomForm(id), data);
  }
  deleteCustomForm(id: string) {
    return apiClient.delete(this.svc.deleteCustomForm(id));
  }
}

export const bookingApi = new BookingApi();
